/** Cinematic event scheduler, NOT a neutron-transport solver.
 * U-235 + n -> U-236* -> Ba-141 + Kr-92 + 3n is ONE representative channel.
 * Scene scales, velocities, cross sections, timing, target selection and all light
 * effects are artistic. Free neutrons/gammas are not visible glowing particles.
 * Most released energy is fragment kinetic energy; the flash is a visual convention.
 * Reference: DOE Nuclear Physics and Reactor Theory, DOE-HDBK-1019/1-93.
 */
export type Vec3 = [number, number, number]
export type NuclearState = 'stable' | 'absorbing' | 'excited' | 'deforming' | 'fissioning' | 'fragments'
export const PLAYBACK_SPEED = 1.25
export const INCIDENT_INTERVAL = 2.2
export const INCIDENT_JITTER = 1.1
export const ABSORPTION = .18
export const EXCITATION = .32
export const SCISSION = 1.18
export const FRAGMENT_END = 4.8
export const NUCLEON_COUNT = 236
export const MAX_NEUTRONS = 28
export const MAX_GAMMAS = 24
export const smooth = (value: number) => { const x = Math.max(0, Math.min(1, value)); return x * x * (3 - 2 * x) }
export function seededRandom(seed: number) {
  let state = seed >>> 0
  return () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296 }
}
export function reactionState(age: number): NuclearState {
  if (age < 0) return 'stable'
  if (age < ABSORPTION) return 'absorbing'
  if (age < EXCITATION) return 'excited'
  if (age < SCISSION - .14) return 'deforming'
  if (age < SCISSION) return 'fissioning'
  return 'fragments'
}
export function sweptSphere(start: Vec3, end: Vec3, center: Vec3, radius: number) {
  const dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2]
  const sx = start[0] - center[0], sy = start[1] - center[1], sz = start[2] - center[2]
  const a = dx * dx + dy * dy + dz * dz
  const b = sx * dx + sy * dy + sz * dz
  const c = sx * sx + sy * sy + sz * sz - radius * radius
  if (c <= 0) return 0
  if (!a) return Infinity
  const discriminant = b * b - a * c
  if (discriminant < 0) return Infinity
  const hit = (-b - Math.sqrt(discriminant)) / a
  return hit >= 0 && hit <= 1 ? hit : Infinity
}
const composition = [
  [.015, .015, 0, 1.9], [.29, .24, -2.4, 1.32], [-.27, -.23, -1.4, 1.36],
  [-.45, .32, 2.6, 2.2], [.46, -.34, 3.8, 2.5], [-.1, .38, -7, .8],
  [.28, -.13, -4, .92], [-.35, .04, -6.5, .7], [.09, -.39, -3, .95],
  [.43, .41, -8, .76], [-.46, -.42, -8, .7], [.19, .45, 1.8, 1.38],
]
export type Nucleus = {
  position: Vec3; entry: Vec3; destination: Vec3; scale: number; age: number; birth: number;
  angle: number; tilt: number; spin: number; emitted: boolean; generation: number; state: NuclearState
}
export type Neutron = { position: Vec3; previous: Vec3; velocity: Vec3; life: number; source: number; target: number; generation: number; history: Float32Array }
export type Gamma = { position: Vec3; velocity: Vec3; life: number }
const vec = (): Vec3 => [0, 0, 0]
const copy = (to: Vec3, from: Vec3) => { to[0] = from[0]; to[1] = from[1]; to[2] = from[2] }
export class ReactionSystem {
  random: () => number
  nuclei: Nucleus[]
  neutrons: Neutron[]
  gammas: Gamma[]
  time = 0
  nextIncident = .65
  incidentInterval = INCIDENT_INTERVAL
  incidentJitter = INCIDENT_JITTER
  private primed = false
  maxEvents = 3
  width: number
  height: number
  framingScale: number
  stats = { absorptions: 0, fissions: 0, secondary: 0, escaped: 0, captures: 0, replenished: 0 }
  constructor(width: number, height: number, count = 12, seed = 1817) {
    this.width = width; this.height = height; this.framingScale = width < height ? .72 : 1; this.random = seededRandom(seed)
    this.nuclei = composition.slice(0, count).map(([x, y, z, scale], index) => {
      const position: Vec3 = [x * width, y * height, z]
      return { position, entry: [...position], destination: [...position], scale: (count === 1 ? 2.8 : scale) * this.framingScale, age: -1, birth: -10 - index, angle: this.random() * 6.28, tilt: this.random() * .7 - .35, spin: .035 + this.random() * .045, emitted: false, generation: 0, state: 'stable' }
    })
    this.neutrons = Array.from({ length: MAX_NEUTRONS }, () => ({ position: vec(), previous: vec(), velocity: vec(), life: 0, source: -1, target: -1, generation: 0, history: new Float32Array(10 * 3) }))
    this.gammas = Array.from({ length: MAX_GAMMAS }, () => ({ position: vec(), velocity: vec(), life: 0 }))
  }
  /** A staged, already-running showcase, not simulated radioactive source rates.
   * Individual nuclei recycle through the existing fixed pool, without restarting
   * the whole scene or allocating more geometry as the chain continues. */
  primeShowcase() {
    if (this.primed || this.nuclei.length < 5) return
    this.primed = true
    this.incidentInterval = .85; this.incidentJitter = .45; this.nextIncident = .2
    const seeds = [[3, .48], [4, SCISSION - .06], [1, SCISSION + .2], [2, SCISSION + .58], [6, .82]]
    const selected = seeds.slice(0, this.maxEvents >= 4 ? 5 : 4).filter(([index]) => index < this.nuclei.length)
    for (const [index, age] of selected) {
      const n = this.nuclei[index]
      n.age = age; n.state = reactionState(age); n.emitted = age >= SCISSION
      this.stats.absorptions++
      if (n.emitted) this.stats.fissions++
    }
    for (const [index] of selected) if (this.nuclei[index].emitted) this.emit(index)
  }
  configure(maxEvents: number) { this.maxEvents = maxEvents }
  activeEvents() { return this.nuclei.filter(n => n.age >= 0 && n.age < SCISSION + .4).length }
  resize(width: number, height: number) {
    const x = width / this.width, y = height / this.height
    const framing = width < height ? .72 : 1
    for (const n of this.nuclei) n.scale *= framing / this.framingScale
    this.framingScale = framing
    for (const n of this.nuclei) for (const p of [n.position, n.entry, n.destination]) { p[0] *= x; p[1] *= y }
    for (const p of this.neutrons) {
      for (const v of [p.position, p.previous, p.velocity]) { v[0] *= x; v[1] *= y }
      for (let i = 0; i < 10; i++) { p.history[i * 3] *= x; p.history[i * 3 + 1] *= y }
    }
    for (const p of this.gammas) { p.position[0] *= x; p.position[1] *= y; p.velocity[0] *= x; p.velocity[1] *= y }
    this.width = width; this.height = height
  }
  launch(from: Vec3, to: Vec3, source: number, target: number, generation: number, duration: number) {
    const p = this.neutrons.find(item => item.life <= 0)
    if (!p) return
    copy(p.position, from); copy(p.previous, from)
    for (let axis = 0; axis < 3; axis++) p.velocity[axis] = (to[axis] - from[axis]) / duration
    for (let i = 0; i < 10; i++) p.history.set(from, i * 3)
    p.life = target >= 0 ? duration + .6 : 2
    p.source = source; p.target = target; p.generation = generation
  }
  incident(target = -1) {
    if (this.activeEvents() >= this.maxEvents) return
    if (target < 0) {
      // First event is prominent; later external neutrons pick a visible stable nucleus.
      const candidates = this.nuclei.map((n, i) => ({ n, i })).filter(({ n }) => n.age < 0 && this.time - n.birth > 2 && Math.abs(n.position[0]) < this.width * .43 && Math.abs(n.position[1]) < this.height * .4)
      target = this.stats.absorptions === 0 ? 0 : candidates[Math.floor(this.random() * candidates.length)]?.i ?? -1
    }
    if (target < 0 || this.nuclei[target].age >= 0) return
    const center = this.nuclei[target].position
    const from: Vec3 = [center[0] - (this.width < this.height ? 3 : 6), center[1] + .6, center[2] + 1.1]
    this.launch(from, center, -1, target, 0, 1.3)
  }
  emit(index: number) {
    const n = this.nuclei[index]
    const targets = this.nuclei.map((other, id) => ({ other, id })).filter(({ other, id }) => id !== index && other.age < 0 && this.time - other.birth > 2 && Math.abs(other.position[0]) < this.width * .46 && Math.abs(other.position[1]) < this.height * .44)
      .sort((a, b) => a.other.position.reduce((sum, x, axis) => sum + (x - n.position[axis]) ** 2, 0) - b.other.position.reduce((sum, x, axis) => sum + (x - n.position[axis]) ** 2, 0))
    for (let i = 0; i < 3; i++) {
      const selected = i < 2 ? targets[i] : undefined
      const angle = this.random() * Math.PI * 2
      const end: Vec3 = selected ? [...selected.other.position] : [n.position[0] + Math.cos(angle) * 8, n.position[1] + Math.sin(angle) * 8, n.position[2] + (this.random() - .5) * 7]
      this.launch(n.position, end, index, selected?.id ?? -1, n.generation + 1, .75 + i * .24 + this.random() * .15)
    }
    for (let i = 0; i < 5; i++) {
      const p = this.gammas.find(item => item.life <= 0)
      if (!p) break
      const angle = this.random() * Math.PI * 2
      copy(p.position, n.position)
      p.velocity = [Math.cos(angle) * 21, Math.sin(angle) * 21, (this.random() - .5) * 22]
      p.life = .2 + this.random() * .12
    }
  }
  replenish(n: Nucleus) {
    const side = this.random() < .5 ? -1 : 1
    n.entry = [side * this.width * .72, (this.random() - .5) * this.height * .75, -2 - this.random() * 6]
    n.destination = [(this.random() - .5) * this.width * .75, (this.random() - .5) * this.height * .72, -1 - this.random() * 5]
    copy(n.position, n.entry)
    n.age = -1; n.birth = this.time; n.emitted = false; n.state = 'stable'
    n.scale = (.85 + this.random() * .35) * this.framingScale
    this.stats.replenished++
  }
  update(delta: number) {
    // Substeps plus swept intersections prevent tunnelling after occasional slow frames.
    const steps = Math.max(1, Math.ceil(Math.min(delta, .12) / .025))
    const dt = Math.min(delta, .12) / steps
    for (let step = 0; step < steps; step++) this.advance(dt)
  }
  private advance(dt: number) {
    this.time += dt
    if (this.time >= this.nextIncident) { this.incident(); this.nextIncident = this.time + this.incidentInterval + this.random() * this.incidentJitter }
    for (let index = 0; index < this.nuclei.length; index++) {
      const n = this.nuclei[index]
      if (n.age < 0) {
        n.angle += n.spin * dt
        if (n.birth > 0) {
          const t = smooth((this.time - n.birth) / 6)
          for (let axis = 0; axis < 3; axis++) n.position[axis] = n.entry[axis] + (n.destination[axis] - n.entry[axis]) * t
        }
      } else {
        n.age += dt; n.state = reactionState(n.age)
        if (n.age >= SCISSION && !n.emitted) { n.emitted = true; this.stats.fissions++; this.emit(index) }
        if (n.age > FRAGMENT_END) this.replenish(n)
      }
    }
    for (const p of this.neutrons) {
      if (p.life <= 0) continue
      copy(p.previous, p.position)
      for (let axis = 0; axis < 3; axis++) p.position[axis] += p.velocity[axis] * dt
      let closest = Infinity, hit = -1
      for (let index = 0; index < this.nuclei.length; index++) {
        const n = this.nuclei[index]
        if (index === p.source || n.age >= 0 || this.time - n.birth < 2) continue
        const intersection = sweptSphere(p.previous, p.position, n.position, n.scale * .78)
        if (intersection < closest) { closest = intersection; hit = index }
      }
      if (hit >= 0) {
        const n = this.nuclei[hit]
        if (this.activeEvents() < this.maxEvents) {
          n.age = 0; n.generation = p.generation; n.state = 'absorbing'; n.emitted = false
          this.stats.absorptions++; if (p.generation > 0) this.stats.secondary++
        } else this.stats.captures++
        p.life = 0
      } else {
        p.life -= dt
        if (p.life <= 0) this.stats.escaped++
      }
      p.history.copyWithin(3, 0, 27); p.history.set(p.position, 0)
    }
    for (const p of this.gammas) {
      if (p.life <= 0) continue
      p.life -= dt
      for (let axis = 0; axis < 3; axis++) p.position[axis] += p.velocity[axis] * dt
    }
  }
}
